import React from 'react';
import PropTypes from 'prop-types';
import DataTable from '../common/DataTable';
import DateTimeDisplay from '../common/DateTimeDisplay';
import UserRoleBadge from '../../../shared/components/UserRoleBadge';
import StatusPill from '../../../shared/components/pill/StatusPill';

// A token is an actor with a role and an owner, so this reads like the users table on purpose:
// same contact column, same role pill, same search-and-sort frame as every other admin list.
export default function ApiTokensTable({ items, onRevoke, revokingId }) {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      icon: 'bi-key',
      sortable: true,
      render: (row) => <span className="fw-semibold">{row.name}</span>,
    },
    {
      key: 'contact',
      label: 'Contact',
      icon: 'bi-person',
      sortable: true,
      filterValue: (row) => `${row.owner?.full_name ?? ''} ${row.owner?.email ?? ''}`.trim(),
      render: (row) => (
        <div>
          <div>{row.owner?.full_name || row.owner?.email || '—'}</div>
          {row.owner?.full_name && row.owner?.email && (
            <div className="small text-muted">{row.owner.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      filterValue: (row) => row.role || '',
      render: (row) => <UserRoleBadge role={row.role} />,
    },
    {
      key: 'key_id',
      label: 'Key ID',
      icon: 'bi-hash',
      sortable: true,
      className: 'text-nowrap',
      render: (row) => <span className="font-monospace small text-muted">{row.key_id}</span>,
    },
    {
      key: 'last_used_at',
      label: 'Last used',
      icon: 'bi-clock-history',
      sortable: true,
      className: 'text-nowrap',
      render: (row) => <DateTimeDisplay isoString={row.last_used_at} fallback="Never" />,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterValue: (row) => (row.revoked_at ? 'revoked' : 'active'),
      render: (row) => (
        <StatusPill tone={row.revoked_at ? 'quiet' : 'success'}>
          {row.revoked_at ? 'Revoked' : 'Active'}
        </StatusPill>
      ),
    },
  ];

  const actions = [
    {
      key: 'revoke',
      label: 'Revoke',
      icon: 'bi-slash-circle',
      onClick: onRevoke,
      // A revoked token stays in the list as a record; there is nothing left to do to it.
      visible: (row) => !row.revoked_at,
      disabled: (row) => revokingId === row.id,
    },
  ];

  return (
    <DataTable
      items={items}
      columns={columns}
      actions={actions}
      filterPlaceholder="Search tokens…"
      emptyMessage="No API tokens yet."
    />
  );
}

ApiTokensTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRevoke: PropTypes.func.isRequired,
  revokingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ApiTokensTable.defaultProps = {
  revokingId: null,
};
